import React from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import {
  Cog8ToothIcon,
  DevicePhoneMobileIcon,
  Battery50Icon,
  CalendarDaysIcon,
  InformationCircleIcon,
  ArchiveBoxIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  QuestionMarkCircleIcon,
  AdjustmentsHorizontalIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  UserIcon,
  ComputerDesktopIcon,
  CpuChipIcon,
  BookOpenIcon,
  FolderIcon,
  ArrowDownTrayIcon,
  Battery100Icon,
  ReceiptPercentIcon,
  CreditCardIcon,
  ArrowUpTrayIcon,
  HeartIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  HomeIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ShieldCheckIcon,
  CubeIcon,
  PaperAirplaneIcon,
  KeyIcon,
  ChartPieIcon,
} from '@heroicons/react/24/outline';

import { default as logo } from '../assets/portal_logo.svg';
import LeftBarMainItem from './LeftBarMainItem';
import {
  SHOW_ASSETS_PERMISSION,
  SHOW_MACHINE_EVENTS_PERMISSION,
  SHOW_BRAND_CONSUMPTIONS_PERMISSION,
  SHOW_TELEMETRY_EVENTS_PERMISSION,
  SHOW_SUPPORT_PERMISSION,
  SHOW_DEPLOYMENTS_PERMISSION,
  SHOW_SANDBOX_OBJECT_PERMISSION,
  SHOW_MESSAGE_SIMULATOR,
  SHOW_CLOUD_TO_DEVICE,
  SHOW_CERTIFICATE_QUOTA,
  SHOW_CERTIFICATE_STATUS,
  IS_ROOT,
  GET_BUNDLES_FOR_GIVEN_BRAND,
  SHOW_FINE_GRAINED_PERMISSIONS,
  SHOW_PLATFORM_HEALTH,
  SHOW_USER_MANAGEMENT_PERMISSION,
  SHOW_MQTT_USER_ANALYTICS_DASHBOARD,
  SHOW_CREDENTIALS_MANAGEMENT,
  SHOW_POLICY_TEMPLATES,
  SHOW_DATA_DICTIONARY,
  // CREATE_NEW_CREDENTIAL_PERMISSION
} from '../utils/constants';
import { config, target } from '../config/awsExports';
import { useProject } from '../config/ProjectContext';
import useRegionFeatureGate from '../hooks/useRegionFeatureGate';
import ExpandableMenuSection from './ExpandableMenuSection';
import { targetName } from '../utils/portalEnvs';
import { useQuery } from '@tanstack/react-query';
import { API, Auth } from 'aws-amplify';
import changelog from '../CHANGELOG.js';

const { env } = config;

// GraphQL query for backend build info
const GET_BUILD_INFO = /* GraphQL */ `
  query GetBuildInfo {
    getBuildInfo {
      ... on GetBuildInfoSuccess {
        buildInfo {
          timestamp
          commit
          target
        }
      }
      ... on InternalServerError {
        message
        statusCode
      }
    }
  }
`;

// Build info dialog component
function BuildInfoDialog({ isOpen, onClose, env }) {
  const { data: backendInfo, isLoading, error } = useQuery(
    ['buildInfo'],
    async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const result = await API.graphql({
          query: GET_BUILD_INFO,
          authToken: user.signInUserSession.accessToken.jwtToken,
        });
        console.log('BuildInfo result:', result);
        return result.data?.getBuildInfo?.buildInfo;
      } catch (err) {
        console.error('BuildInfo error:', err);
        throw err;
      }
    },
    { enabled: isOpen, staleTime: 60000, retry: false }
  );

  if (!isOpen) return null;
  
  const formatDate = (isoString) => {
    if (!isoString || isoString === 'N/A') return 'N/A';
    try {
      return new Date(isoString).toLocaleString('es-ES', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return isoString; }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4 text-gray-800">🔧 Build Info</h3>
        <div className="space-y-3 text-sm">
          <div>
            <p className="text-gray-500 text-xs">Frontend Deploy</p>
            <p className="text-gray-800">{formatDate(env.buildTimestamp)}</p>
            <p className="text-gray-500 text-xs mt-1">Commit: {env.buildCommit || 'N/A'}</p>
          </div>
          <div className="border-t pt-3">
            <p className="text-gray-500 text-xs">Backend Deploy</p>
            {isLoading ? (
              <p className="text-gray-400 text-xs">Loading...</p>
            ) : error ? (
              <p className="text-red-400 text-xs italic">Error: {error.message || 'Check console'}</p>
            ) : backendInfo ? (
              <>
                <p className="text-gray-800">{formatDate(backendInfo.timestamp)}</p>
                <p className="text-gray-500 text-xs mt-1">Commit: {backendInfo.commit || 'N/A'}</p>
                <p className="text-gray-500 text-xs">Target: {backendInfo.target || 'N/A'}</p>
              </>
            ) : (
              <p className="text-gray-400 text-xs italic">No data</p>
            )}
          </div>
        </div>
        <button 
          onClick={onClose}
          className="mt-4 w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded text-sm"
        >
          Close
        </button>
      </div>
    </div>
  );
}

const ICON_CLASS = "h-6 w-6";

const leftBarDeviceTelemetryListBase = [
  {
    name: 'Overview', path: '/statistics/deviceOverview', permission: SHOW_MACHINE_EVENTS_PERMISSION, icon: <DevicePhoneMobileIcon className={ICON_CLASS} />,
  },
  {
    name: 'Device Consumptions', path: '/statistics/brandConsumptions', permission: SHOW_BRAND_CONSUMPTIONS_PERMISSION, icon: <Battery50Icon className={ICON_CLASS} />,
  },
  {
    name: 'Device Events', path: '/statistics/brandEvents', permission: SHOW_TELEMETRY_EVENTS_PERMISSION, icon: <CalendarDaysIcon className={ICON_CLASS} />,
  },
  {
    name: 'Device Info', path: '/statistics/deviceInfo', permission: SHOW_TELEMETRY_EVENTS_PERMISSION, icon: <InformationCircleIcon className={ICON_CLASS} />,
  },
  {
    name: 'Device Settings', path: '/statistics/deviceSettings', permission: SHOW_TELEMETRY_EVENTS_PERMISSION, icon: <AdjustmentsHorizontalIcon className={ICON_CLASS} />,
  },
  {
    name: 'Device Raw Data', path: '/statistics/deviceRawData', permission: SHOW_SANDBOX_OBJECT_PERMISSION, icon: <ArchiveBoxIcon className={ICON_CLASS} />,
  },
  {
    name: 'Data Dictionary', path: '/data-dictionary', permission: SHOW_DATA_DICTIONARY, icon: <DocumentDuplicateIcon className={ICON_CLASS} />,
  },
];

const leftBarAssetManagementList = [
  {
    name: 'Assets', path: '/assets', permission: SHOW_ASSETS_PERMISSION, icon: <DocumentTextIcon className={ICON_CLASS} />,
  },
  {
    name: 'Bundles', path: '/bundles', permission: GET_BUNDLES_FOR_GIVEN_BRAND, icon: <FolderIcon className={ICON_CLASS} />,
  },
  {
    name: 'Deployments', path: '/deployments', permission: SHOW_DEPLOYMENTS_PERMISSION, icon: <ClipboardDocumentListIcon className={ICON_CLASS} />,
  },
  {
    name: 'Waves', path: '/waves', permission: SHOW_DEPLOYMENTS_PERMISSION, icon: <ClipboardDocumentListIcon className={ICON_CLASS} />,
  },
];

const leftBarMessageSimulatorList = [
  {
    name: 'Cloud To Device', path: '/cloudToDevice', permission: SHOW_CLOUD_TO_DEVICE, icon: <ArrowDownTrayIcon className={ICON_CLASS} />,
  },
];

if (target === 'DEV' || target === 'STAG') {
  leftBarMessageSimulatorList.push({
    name: 'Device To Cloud', path: '/deviceToCloud', permission: SHOW_CLOUD_TO_DEVICE, icon: <ArrowUpTrayIcon className={ICON_CLASS} />,
  });
}

const leftBarCertificateManagementList = [
  {
    name: 'Quota Management', path: '/quotaManagement', permission: SHOW_CERTIFICATE_QUOTA, icon: <Battery100Icon className={ICON_CLASS} />,
  },
  {
    name: 'Credentials Management', path: '/credentialsManagement', permission: SHOW_CREDENTIALS_MANAGEMENT, icon: <CreditCardIcon className={ICON_CLASS} />,
  },
  {
    name: 'Certificate Status', path: '/certificateStatus', permission: SHOW_CERTIFICATE_STATUS, icon: <ReceiptPercentIcon className={ICON_CLASS} />,
  },
  {
    name: 'Policy Templates', path: '/policyTemplates', permission: SHOW_POLICY_TEMPLATES, icon: <DocumentDuplicateIcon className={ICON_CLASS} />,
  },
];

const leftBarADMIN = [
  {
    name: 'Users',
    permissions: [SHOW_USER_MANAGEMENT_PERMISSION],
    items: [
      {
        name: 'User Management', path: '/userManagement', permission: SHOW_USER_MANAGEMENT_PERMISSION, icon: <UserIcon className={ICON_CLASS} />,
      },
      {
        name: 'User Reports', path: '/listCognitoUsers', permission: IS_ROOT, icon: <ChartBarIcon className={ICON_CLASS} />,
      },
    ],
  },
  {
    name: 'Analytics',
    permissions: [IS_ROOT],
    items: [
      {
        name: 'REST API Analytics', path: '/APIAnalyticsDashboard', permission: IS_ROOT, icon: <CpuChipIcon className={ICON_CLASS} />,
      },
      {
        name: 'Portal KPIS', path: '/kpis', permission: IS_ROOT, icon: <ArrowTrendingUpIcon className={ICON_CLASS} />,
      },
    ],
  },
  {
    name: 'Fine Grained Permissions', path: '/fineGrainedPermissions', permission: SHOW_FINE_GRAINED_PERMISSIONS, icon: <BookOpenIcon className={ICON_CLASS} />,
  },
  {
    name: 'Useful Links', path: '/portalInfo', permission: IS_ROOT, icon: <BookOpenIcon className={ICON_CLASS} />,
  },
  {
    name: 'Configuration', path: '/portalConfiguration', permission: IS_ROOT, icon: <Cog8ToothIcon className={ICON_CLASS} />,
  },
  {
    name: 'Platform Health', path: '/platformHealth', permission: SHOW_PLATFORM_HEALTH, icon: <HeartIcon className={ICON_CLASS} />,
  },
];

export default function LeftBar({ open, setOpen, collapsed, setCollapsed }) {
  const location = useLocation();
  const { project } = useProject();
  const isFeatureAvailable = useRegionFeatureGate();

  // Function to determine which section should be active based on current route
  const getActiveSectionFromRoute = React.useCallback((pathname) => {
    // Handle root path first - this should always go to Home
    // Check for exact match and also handle potential trailing slashes or empty strings
    if (pathname === '/' || pathname === '' || pathname.trim() === '/' || pathname === '/home') {
      return null; // Home is a standalone item, not in an expandable section
    }
    
    // Device Management routes
    if (pathname.startsWith('/statistics/deviceOverview') || 
        pathname.startsWith('/statistics/brandConsumptions') ||
        pathname.startsWith('/statistics/brandEvents') ||
        pathname.startsWith('/statistics/deviceInfo') ||
        pathname.startsWith('/statistics/deviceSettings') ||
        pathname.startsWith('/statistics/deviceRawData') ||
        pathname.startsWith('/statistics/moduleSettings') ||
        pathname.startsWith('/data-dictionary')) {
      return 'Device Management';
    }
    
    // Handle deployment creation routes based on source parameter
    if (pathname.includes('/deployments/create/bundle')) {
      return 'Asset Management'; // Bundle deployments belong to Asset Management
    }
    if (pathname.includes('/deployments/create/asset')) {
      return 'Asset Management'; // Asset deployments also belong to Asset Management
    }
    
    // Asset Management routes
    if (pathname.startsWith('/assets') || 
        pathname.startsWith('/bundles') ||
        pathname.startsWith('/deployments') ||
        pathname.startsWith('/waves')) {
      return 'Asset Management';
    }
    
    // Certificate Management routes
    if (pathname.startsWith('/quotaManagement') ||
        pathname.startsWith('/credentialsManagement') ||
        pathname.startsWith('/certificateStatus') ||
        pathname.startsWith('/policyTemplates')) {
      return 'Certificate Management';
    }
    
    // Message Simulator routes
    if (pathname.startsWith('/cloudToDevice') ||
        pathname.startsWith('/deviceToCloud')) {
      return 'Message Simulator';
    }
    
    // Admin routes
    if (pathname.startsWith('/userManagement') ||
        pathname.startsWith('/listCognitoUsers') ||
        pathname.startsWith('/APIAnalyticsDashboard') ||
        pathname.startsWith('/kpis') ||
        pathname.startsWith('/fineGrainedPermissions') ||
        pathname.startsWith('/portalInfo') ||
        pathname.startsWith('/portalConfiguration') ||
        pathname.startsWith('/platformHealth') ||
        pathname.startsWith('/userActivity') ||
        pathname.startsWith('/notifications')) {
      return 'Admin';
    }
    
    return null;
  }, []);

  // Initialize activeSection state with the correct section based on current route
  const [activeSection, setActiveSection] = React.useState(() => {
    const initialSection = getActiveSectionFromRoute(location.pathname);
    return initialSection;
  });

  // Update active section when route changes
  React.useEffect(() => {
    const newActiveSection = getActiveSectionFromRoute(location.pathname);
    // Always set the active section based on the route, don't compare with current state
    // This ensures the menu is properly expanded when entering the app
    setActiveSection(newActiveSection);
  }, [location.pathname, getActiveSectionFromRoute]);

  const handleDrawer = React.useCallback(() => {
    setOpen(prevOpen => !prevOpen);
  }, [setOpen]);

  const handleSetActive = React.useCallback((sectionName) => {
    setActiveSection(currentActiveSection => currentActiveSection === sectionName ? null : sectionName);
  }, [setActiveSection]);

  const onToggleAdmin = React.useCallback(() => handleSetActive('Admin'), [handleSetActive]);
  const onToggleDeviceManagement = React.useCallback(() => handleSetActive('Device Management'), [handleSetActive]);
  const onToggleAssetManagement = React.useCallback(() => handleSetActive('Asset Management'), [handleSetActive]);
  const onToggleMessageSimulator = React.useCallback(() => handleSetActive('Message Simulator'), [handleSetActive]);
  const onToggleCertificateManagement = React.useCallback(() => handleSetActive('Certificate Management'), [handleSetActive]);

  const closeDrawerMobileHandler = React.useCallback(() => {
    handleSetActive(null);
    handleDrawer();
  }, [handleSetActive, handleDrawer]);

  const closeDrawerDesktopHandler = React.useCallback(() => {
    handleSetActive(null);
  }, [handleSetActive]);

  const latestChangelogEntry = changelog[0];
  const portalVersion = latestChangelogEntry ? `Portal: v ${latestChangelogEntry.version}` : '';
  const ENV_VERSION = `v ${env.version}`;
  const [showBuildInfo, setShowBuildInfo] = React.useState(false);

  const leftBarDeviceTelemetryList = React.useMemo(() => {
    const base = (project?.code === 23 || project?.code === 35 || project?.code === 36 || project?.code === 37 || project?.code === 38)
      ? leftBarDeviceTelemetryListBase.filter((item) => item.name !== 'Device Consumptions') : leftBarDeviceTelemetryListBase;
    // Hide "Data Dictionary" when the China data region is selected (not available there).
    return isFeatureAvailable('dataDictionary') ? base : base.filter((item) => item.name !== 'Data Dictionary');
  }, [project?.code, isFeatureAvailable]);

  // Hide "Platform Health" from the Admin section when the China data region is selected.
  const leftBarADMINFiltered = React.useMemo(() => (
    isFeatureAvailable('platformHealth') ? leftBarADMIN : leftBarADMIN.filter((item) => item.name !== 'Platform Health')
  ), [isFeatureAvailable]);

  const titleName = `Consumer IoT Portal (${targetName[target]})`;

  const expandSidebar = React.useCallback(() => {
    if (setCollapsed) setCollapsed(false);
  }, [setCollapsed]);

  const drawerContent = React.useCallback((isMobile = false) => {
    const isCollapsed = !isMobile && collapsed;
    return (
    <nav className="flex-grow overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 p-2 space-y-1">
      <LeftBarMainItem
        name="Home"
        path="/home"
        permission={SHOW_DEPLOYMENTS_PERMISSION}
        icon={<HomeIcon className={ICON_CLASS} />}
        closeDrawer={isMobile ? closeDrawerMobileHandler : closeDrawerDesktopHandler}
        collapsed={isCollapsed}
      />

      {isFeatureAvailable('admin') && (
        <ExpandableMenuSection
          name="Admin"
          permissions={[
            IS_ROOT,
            SHOW_USER_MANAGEMENT_PERMISSION,
            SHOW_FINE_GRAINED_PERMISSIONS,
            SHOW_PLATFORM_HEALTH,
          ]}
          items={leftBarADMINFiltered}
          icon={<ShieldCheckIcon className={ICON_CLASS} />}
          isActive={activeSection === 'Admin'}
          onToggle={onToggleAdmin}
          closeMobileDrawer={isMobile ? handleDrawer : undefined}
          collapsed={isCollapsed}
          onExpandSidebar={expandSidebar}
        />
      )}

      <ExpandableMenuSection
        name="Device Management"
        permissions={[SHOW_MACHINE_EVENTS_PERMISSION, SHOW_BRAND_CONSUMPTIONS_PERMISSION, SHOW_TELEMETRY_EVENTS_PERMISSION, SHOW_SANDBOX_OBJECT_PERMISSION, SHOW_DATA_DICTIONARY]}
        items={leftBarDeviceTelemetryList}
        icon={<ComputerDesktopIcon className={ICON_CLASS} />}
        isActive={activeSection === 'Device Management'}
        onToggle={onToggleDeviceManagement}
        closeMobileDrawer={isMobile ? handleDrawer : undefined}
        collapsed={isCollapsed}
        onExpandSidebar={expandSidebar}
      />

      {project?.code === 5 && (
        <LeftBarMainItem
          name="Module Settings"
          path="/statistics/moduleSettings"
          permission={SHOW_TELEMETRY_EVENTS_PERMISSION}
          icon={<Cog8ToothIcon className={ICON_CLASS} />}
          closeDrawer={isMobile ? closeDrawerMobileHandler : closeDrawerDesktopHandler}
          collapsed={isCollapsed}
        />
      )}

      <ExpandableMenuSection
        name="Asset Management"
        permissions={[SHOW_ASSETS_PERMISSION, SHOW_DEPLOYMENTS_PERMISSION, GET_BUNDLES_FOR_GIVEN_BRAND]}
        items={leftBarAssetManagementList}
        icon={<CubeIcon className={ICON_CLASS} />}
        isActive={activeSection === 'Asset Management'}
        onToggle={onToggleAssetManagement}
        closeMobileDrawer={isMobile ? handleDrawer : undefined}
        collapsed={isCollapsed}
        onExpandSidebar={expandSidebar}
      />

      {isFeatureAvailable('messageSimulator') && (
        <ExpandableMenuSection
          name="Message Simulator"
          permissions={[SHOW_MESSAGE_SIMULATOR]}
          items={leftBarMessageSimulatorList}
          icon={<PaperAirplaneIcon className={ICON_CLASS} />}
          isActive={activeSection === 'Message Simulator'}
          onToggle={onToggleMessageSimulator}
          closeMobileDrawer={isMobile ? handleDrawer : undefined}
          collapsed={isCollapsed}
          onExpandSidebar={expandSidebar}
        />
      )}

      {isFeatureAvailable('certificateManagement') && (
        <ExpandableMenuSection
          name="Certificate Management"
          permissions={[SHOW_CERTIFICATE_QUOTA, SHOW_CERTIFICATE_STATUS, SHOW_POLICY_TEMPLATES]}
          items={leftBarCertificateManagementList}
          icon={<KeyIcon className={ICON_CLASS} />}
          isActive={activeSection === 'Certificate Management'}
          onToggle={onToggleCertificateManagement}
          closeMobileDrawer={isMobile ? handleDrawer : undefined}
          collapsed={isCollapsed}
          onExpandSidebar={expandSidebar}
        />
      )}

      {isFeatureAvailable('mqttDashboard') && (
        <LeftBarMainItem
          name="Dashboard"
          path="/dashboard-by-project"
          permission={SHOW_MQTT_USER_ANALYTICS_DASHBOARD}
          icon={<ChartPieIcon className={ICON_CLASS} />}
          closeDrawer={isMobile ? closeDrawerMobileHandler : closeDrawerDesktopHandler}
          collapsed={isCollapsed}
        />
      )}

      <LeftBarMainItem
        name="Service Request"
        path="/support"
        permission={SHOW_SUPPORT_PERMISSION}
        icon={<QuestionMarkCircleIcon className={ICON_CLASS} />}
        closeDrawer={isMobile ? closeDrawerMobileHandler : closeDrawerDesktopHandler}
        collapsed={isCollapsed}
      />
    </nav>
    );
  }, [activeSection, handleDrawer, leftBarDeviceTelemetryList, leftBarADMINFiltered, isFeatureAvailable, project?.code, onToggleAdmin, onToggleDeviceManagement, onToggleAssetManagement, onToggleMessageSimulator, onToggleCertificateManagement, closeDrawerMobileHandler, closeDrawerDesktopHandler, collapsed, expandSidebar]);

  return (
    <>
      {open && <div className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden" onClick={handleDrawer}></div>}
      <div
        className={`fixed inset-y-0 left-0 z-40 w-[280px] bg-white shadow-xl flex flex-col transform transition-transform duration-300 ease-in-out md:hidden ${open ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        <div className="flex items-center justify-between pl-4 border-b flex-shrink-0">
          <h2 className="text-md text-gray-800">{titleName}</h2>
          <button onClick={handleDrawer} className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        {drawerContent(true)}
      </div>

      <div className={`hidden md:flex md:flex-col ${collapsed ? 'md:w-16' : 'md:w-[280px]'} bg-white border-r fixed left-0 top-[16px] h-[calc(100vh-56px)] z-20 shadow-sm transition-[width] duration-200 ease-in-out`}>
        <div className={`flex items-center ${collapsed ? 'justify-center px-2' : 'pl-3 pr-2'} pb-3 border-b flex-shrink-0`}>
          {!collapsed && (
            <>
              <img src={logo} alt="Logo" className="h-8 w-auto opacity-90" />
              <h2 className="text-md text-gray-800 ml-3 flex-grow truncate">{titleName}</h2>
            </>
          )}
          <button
            onClick={() => setCollapsed && setCollapsed(!collapsed)}
            className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronDoubleRightIcon className="h-5 w-5" />
            ) : (
              <ChevronDoubleLeftIcon className="h-5 w-5" />
            )}
          </button>
        </div>
        {drawerContent(false)}
        {!collapsed && (
          <div className="p-4 border-t flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{ENV_VERSION}</p>
                {portalVersion && <p className="text-xs text-gray-500 mt-1">{portalVersion}</p>}
              </div>
              <button 
                onClick={() => setShowBuildInfo(true)}
                className="text-gray-300 hover:text-gray-500 transition-colors p-1"
                title="Build Info"
              >
                <InformationCircleIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
      <BuildInfoDialog isOpen={showBuildInfo} onClose={() => setShowBuildInfo(false)} env={env} />
    </>
  );
}

LeftBar.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  collapsed: PropTypes.bool,
  setCollapsed: PropTypes.func,
};

LeftBar.defaultProps = {
  collapsed: false,
  setCollapsed: undefined,
};
